<template>
    <div class="friend-flow-wrapper">
        <!-- PHONE NUMBER -->
        <div v-if="mode === 'phone'" class="friend-phone-card">
            <div v-if="phonePromptMessage" class="friend-phone-message">
                {{ phonePromptMessage }}
            </div>
            <div class="friend-phone-row">
                <Dropdown v-model="phoneForm.countryCodeId"
                          :options="normalizedCountryCodes"
                          optionValue="value"
                          optionLabel="text"
                          placeholder="Code"
                          filter
                          class="friend-country-dropdown"
                          :disabled="disabled || loading || phoneSubmitted"
                          @change="onCountryCodeChange" />
                <input v-model="phoneForm.phoneNumber"
                       type="text"
                       inputmode="numeric"
                       class="friend-phone-input"
                       :placeholder="phonePlaceholder"
                       :disabled="disabled || loading || phoneSubmitted"
                       @input="onPhoneInput"
                       @keyup.enter="submitPhone" />
            </div>
            <div v-if="phoneError" class="friend-phone-error">
                {{ phoneError }}
            </div>
            <button type="button"
                    class="friend-phone-submit"
                    :disabled="disabled || loading || phoneSubmitted || !isPhoneComplete"
                    @click="submitPhone">
                {{ loading ? 'Please wait...' : 'Continue' }}
            </button>
        </div>
        <!-- PREVIEW / FORM -->
        <div v-else-if="mode === 'preview' || mode === 'form'" class="friend-card">
            <div class="friend-card-header">
                <div>
                    <div class="friend-card-title">Friend Summary</div>
                    <div class="friend-card-subtitle">
                        {{ cleanMessage || 'Please review before adding' }}
                    </div>
                </div>
            </div>
            <div class="friend-card-body">
                <div class="friend-detail-row friend-edit-row">
                    <span class="friend-label">First Name</span>
                    <input v-model="editForm.firstName"
                           type="text"
                           class="friend-edit-input"
                           placeholder="First Name"
                           :disabled="disabled || loading || submitted"
                           @input="onNameFieldInput" />
                </div>
                <div class="friend-detail-row friend-edit-row">
                    <span class="friend-label">Last Name</span>
                    <input v-model="editForm.lastName"
                           type="text"
                           class="friend-edit-input"
                           placeholder="Last Name"
                           :disabled="disabled || loading || submitted"
                           @input="onNameFieldInput" />
                </div>
                <div class="friend-detail-row friend-edit-row">
                    <span class="friend-label">Email</span>
                    <input v-model="editForm.email"
                           type="email"
                           class="friend-edit-input"
                           placeholder="Email"
                           :disabled="disabled || loading || submitted"
                           @input="onNameFieldInput" />
                </div>
                <div class="friend-detail-row friend-edit-row">
                    <span class="friend-label">Country Code</span>
                    <Dropdown v-model="phoneForm.countryCodeId"
                              :options="normalizedCountryCodes"
                              optionValue="value"
                              optionLabel="text"
                              placeholder="Select Country Code"
                              filter
                              class="friend-preview-country"
                              :disabled="disabled || loading || submitted"
                              @change="onCountryCodeChange" />
                </div>
                <div class="friend-detail-row friend-edit-row no-border">
                    <span class="friend-label">Phone</span>
                    <input v-model="phoneForm.phoneNumber"
                           type="text"
                           inputmode="numeric"
                           class="friend-preview-phone"
                           :placeholder="phonePlaceholder"
                           :disabled="disabled || loading || submitted"
                           @input="onPhoneInput" />
                </div>
            </div>
            <div class="friend-actions">
                <button type="button"
                        class="friend-primary-btn"
                        :disabled="disabled || loading || submitted"
                        @click="confirmFriend">
                    {{ loading ? 'Please wait...' : 'Save' }}
                </button>
                <button type="button"
                        class="friend-cancel-btn"
                        :disabled="disabled || loading"
                        @click="$emit('cancel')">
                    Cancel
                </button>
            </div>
        </div>
        <!-- FRIEND LIST -->
        <div v-else-if="mode === 'list'" class="friend-card friend-list-card">
            <div class="friend-card-header friend-list-header">
                <div>
                    <div class="friend-card-title">Your Friends</div>
                    <div v-if="cleanMessage" class="friend-card-subtitle">{{ cleanMessage }}</div>
                </div>
                <span class="friend-count">{{ friends.length }}</span>
            </div>
            <div v-if="friends.length" class="friend-list">
                <div v-for="(friend, index) in friends"
                     :key="friendKey(friend, index)"
                     class="friend-list-item">
                    <div class="friend-list-top">
                        <div class="friend-avatar" :style="{ background: avatarColor(friend, index) }">
                            {{ getFriendInitials(friend) }}
                        </div>
                        <div class="friend-info">
                            <div class="friend-name">{{ getFriendName(friend) }}</div>
                        </div>
                        <div class="friend-list-actions">
                            <button type="button"
                                    class="friend-small-btn"
                                    :disabled="disabled || loading"
                                    @click="toggleDetails(friend, index)">
                                {{ isDetailsOpen(friend, index) ? 'Hide' : 'Details' }}
                            </button>
                            <button type="button"
                                    class="friend-small-btn friend-remove-btn"
                                    :disabled="disabled || loading"
                                    @click="requestDelete(friend)">
                                Remove
                            </button>
                        </div>
                    </div>
                    <div v-if="isDetailsOpen(friend, index)" class="friend-details">
                        <div class="friend-detail-row">
                            <span class="friend-label">Email</span>
                            <span class="friend-value">{{ getFriendEmail(friend) }}</span>
                        </div>
                        <div class="friend-detail-row" :class="{ 'no-border': getFriendAsset(friend) === '-' }">
                            <span class="friend-label">Created On</span>
                            <span class="friend-value">{{ getFriendCreatedOn(friend) }}</span>
                        </div>
                        <div v-if="getFriendAsset(friend) !== '-'" class="friend-detail-row no-border">
                            <span class="friend-label">Asset</span>
                            <span class="friend-value">{{ getFriendAsset(friend) }}</span>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else class="friend-empty-state">No friends found.</div>
        </div>
        <!-- CREATED / FAILED -->
        <div v-else-if="mode === 'success' || mode === 'error'" class="friend-card">
            <div class="friend-card-header" :class="{ 'friend-error-header': resultIsError }">
                <div class="friend-card-title">
                    {{ resultIsError ? 'Friend Not Added' : 'Friend Added' }}
                </div>
                <div v-if="cleanMessage" class="friend-card-subtitle">{{ cleanMessage }}</div>
            </div>
            <div class="friend-card-body">
                <div class="friend-detail-row">
                    <span class="friend-label">Name</span>
                    <span class="friend-value">{{ resultName }}</span>
                </div>
                <div class="friend-detail-row">
                    <span class="friend-label">Email</span>
                    <span class="friend-value">{{ resultEmail }}</span>
                </div>
                <div v-if="resultPhone !== '-'" class="friend-detail-row no-border">
                    <span class="friend-label">Phone Number</span>
                    <span class="friend-value">{{ resultPhone }}</span>
                </div>
                <div v-if="resultIsError" class="friend-error">
                    {{ friendData.error || friendData.message || 'Please try again.' }}
                </div>
            </div>
            <div class="friend-result-footer">
                <span>{{ currentDate }}</span>
                <span :class="resultIsError ? 'friend-status-error' : 'friend-status-success'">
                    {{ resultIsError ? 'Not Saved' : 'Saved' }}
                </span>
            </div>
        </div>
    </div>
</template>
<script>
    import Dropdown from 'primevue/dropdown';
    import { resolvePhoneLength } from '@/helpers/phoneCountryHelpers.js';
    export default {
        name: 'ManageFriends',
        components: { Dropdown },
        props: {
            mode: { type: String, default: '' },
            message: { type: String, default: '' },
            friendData: { type: Object, default: () => ({}) },
            countryCodeList: { type: Array, default: () => [] },
            selectedCountryCodeText: { type: String, default: '' },
            currentDate: { type: String, default: '' },
            loading: { type: Boolean, default: false },
            disabled: { type: Boolean, default: false },
            isError: { type: Boolean, default: false }
        },
        emits: [
            'submit',
            'cancel',
            'delete-friend',
            'phone-submit',
            'phone-validity-change'
        ],
        data() {
            return {
                phoneForm: {
                    countryCodeId: '',
                    countryCodeText: '',
                    phoneNumber: ''
                },
                editForm: {
                    firstName: '',
                    lastName: '',
                    email: ''
                },
                phoneError: '',
                phoneSubmitted: false,
                submitted: false,
                expandedFriendKey: '',
                avatarPalette: [
                    '#7C3AED',
                    '#0F66B8',
                    '#16A34A',
                    '#F39C12',
                    '#DB2777',
                    '#0891B2',
                    '#DC2626',
                    '#4F46E5'
                ]
            };
        },
        computed: {
            cleanMessage() {
                return String(this.message || '').replace(/\*\*/g, '').trim();
            },
            phonePromptMessage() {
                return (
                    this.cleanMessage ||
                    String(
                        this.friendData?.response ||
                        this.friendData?.message ||
                        ''
                    ).replace(/\*\*/g, '').trim() ||
                    'What is your friend\'s Phone Number?'
                );
            },
            prefilled() {
                return this.friendData?.prefilled || this.friendData?.friend || {};
            },
            normalizedCountryCodes() {
                return (this.countryCodeList || []).map(item => ({
                    value: String(
                        item?.value ?? item?.id ?? item?.Id ??
                        item?.countryCodeId ?? item?.CountryCodeId ?? ''
                    ),
                    text: String(
                        item?.text ?? item?.name ?? item?.Name ??
                        item?.countryCodeText ?? item?.CountryCodeText ??
                        item?.countryCode ?? item?.CountryCode ?? ''
                    )
                })).filter(item => item.value || item.text);
            },
            selectedCountryCode() {
                if (!this.phoneForm.countryCodeId) {
                    return null;
                }
                return this.normalizedCountryCodes.find(
                    item => String(item.value) === String(this.phoneForm.countryCodeId)
                ) || null;
            },
            requiredPhoneLength() {
                const length = Number(resolvePhoneLength(
                    this.phoneForm.countryCodeId,
                    this.countryCodeList || []
                ));
                return length > 0 ? length : 10;
            },
            phonePlaceholder() {
                return this.buildPhonePlaceholder(
                    Number(this.requiredPhoneLength || 10)
                );
            },
            isPhoneComplete() {
                return this.phoneDigits.length === Number(this.requiredPhoneLength || 10);
            },
            phoneDigits() {
                return String(this.phoneForm.phoneNumber || '').replace(/\D/g, '');
            },
            friends() {
                const candidates = [
                    this.friendData?.friends,
                    this.friendData?.data,
                    this.friendData?.items,
                    this.friendData?.friendList
                ];
                return candidates.find(Array.isArray) || [];
            },
            previewCountryCode() {
                let country = String(
                    this.prefilled.countryCodeText ||
                    this.prefilled.CountryCodeText ||
                    ''
                ).trim();
                if (
                    !country ||
                    country === 'Loading…' ||
                    country === 'Loading...'
                ) {
                    country = String(
                        this.selectedCountryCodeText ||
                        this.selectedCountryCode?.text ||
                        this.phoneForm.countryCodeText ||
                        ''
                    ).trim();
                }
                return country || '-';
            },
            previewPhone() {
                let phone = String(
                    this.prefilled.phoneNumber ||
                    this.prefilled.PhoneNumber ||
                    this.prefilled.phone ||
                    ''
                ).trim();
                if (!phone) {
                    return '-';
                }
                phone = phone.replace(/^\+\d+\s*/, '').trim();
                return phone || '-';
            },
            friendAssetName() {
                return this.valueOrDash(
                    this.prefilled.assetName ||
                    this.prefilled.AssetName ||
                    this.prefilled.propertyName
                );
            },
            resultIsError() {
                return this.isError || this.mode === 'error';
            },
            resultData() {
                return this.friendData?.result || this.friendData?.friend || this.prefilled || {};
            },
            resultName() {
                const fullName = [
                    this.resultData.firstName || this.resultData.FirstName,
                    this.resultData.lastName || this.resultData.LastName
                ].filter(Boolean).join(' ');
                return this.valueOrDash(
                    fullName || this.resultData.name || this.resultData.friendName
                );
            },
            resultEmail() {
                return this.valueOrDash(this.resultData.email || this.resultData.Email);
            },
            resultPhone() {
                return this.valueOrDash(
                    this.resultData.phoneNumber || this.resultData.PhoneNumber || this.resultData.phone
                );
            }
        },
        watch: {
            prefilled: {
                immediate: true,
                handler(value) {
                    const pre = value || {};
                    if (!this.editForm.firstName && !this.editForm.lastName && !this.editForm.email) {
                        this.editForm.firstName = String(pre.firstName || pre.FirstName || '');
                        this.editForm.lastName = String(pre.lastName || pre.LastName || '');
                        this.editForm.email = String(pre.email || pre.Email || '');
                    }
                    const prefilledPhone = String(
                        pre.phone || pre.phoneNumber || pre.PhoneNumber || ''
                    );
                    if (!this.phoneDigits && prefilledPhone) {
                        this.phoneForm.phoneNumber = prefilledPhone;
                    }
                    const incomingCountryCodeId = String(
                        pre.countryCodeId || pre.CountryCodeId || ''
                    );
                    if (incomingCountryCodeId) {
                        this.phoneForm.countryCodeId = incomingCountryCodeId;
                    }
                    let countryText = String(
                        pre.countryCodeText || pre.CountryCodeText || ''
                    ).trim();
                    if (countryText === 'Loading…' || countryText === 'Loading...') {
                        countryText = '';
                    }
                    if (countryText) {
                        this.phoneForm.countryCodeText = countryText;
                    } else if (this.selectedCountryCodeText) {
                        this.phoneForm.countryCodeText = this.selectedCountryCodeText;
                    }
                }
            },
            normalizedCountryCodes: {
                immediate: true,
                handler(items) {
                    if (!items.length) {
                        return;
                    }
                    if (this.phoneForm.countryCodeId) {
                        const existing = items.find(
                            item => String(item.value) === String(this.phoneForm.countryCodeId)
                        );
                        if (existing) {
                            this.phoneForm.countryCodeText =
                                this.phoneForm.countryCodeText ||
                                existing.text ||
                                '';
                            return;
                        }
                    }
                    const preferred =
                        items.find(item => /\(US\)|\bUS\b/i.test(String(item.text || ''))) ||
                        items.find(item => String(item.text || '').trim().startsWith('+1')) ||
                        items[0];
                    if (preferred) {
                        this.phoneForm.countryCodeId = preferred.value;
                        this.phoneForm.countryCodeText = preferred.text || '';
                    }
                }
            }
        },
        methods: {
            valueOrDash(value) {
                const text = String(value ?? '').trim();
                return text || '-';
            },
            onCountryCodeChange() {
                const selected = this.normalizedCountryCodes.find(
                    item => String(item.value) === String(this.phoneForm.countryCodeId)
                );
                this.phoneForm.countryCodeText = selected?.text || '';
                this.phoneForm.phoneNumber = '';
                this.phoneError = '';
                this.phoneSubmitted = false;
                this.emitPhoneValidity();
            },
            buildPhonePlaceholder(length) {
                const requiredLength = Number(length || 10);
                if (requiredLength === 10) return '(000)-000-0000';
                if (requiredLength === 7) return '000-0000';
                if (requiredLength === 8) return '0000-0000';
                if (requiredLength === 9) return '000-000-000';
                if (requiredLength === 11) return '000-0000-0000';
                if (requiredLength > 0) return '0'.repeat(requiredLength);
                return 'Phone number';
            },
            formatPhoneByLength(digits, length) {
                const clean = String(digits || '').replace(/\D/g, '');
                const requiredLength = Number(length || 10);
                if (requiredLength === 10) {
                    if (clean.length > 6) return `(${clean.slice(0, 3)})-${clean.slice(3, 6)}-${clean.slice(6)}`;
                    if (clean.length > 3) return `(${clean.slice(0, 3)})-${clean.slice(3)}`;
                    return clean.length ? `(${clean}` : '';
                }
                if (requiredLength === 7) return clean.length > 3 ? `${clean.slice(0, 3)}-${clean.slice(3)}` : clean;
                if (requiredLength === 8) return clean.length > 4 ? `${clean.slice(0, 4)}-${clean.slice(4)}` : clean;
                if (requiredLength === 9) {
                    if (clean.length > 6) return `${clean.slice(0, 3)}-${clean.slice(3, 6)}-${clean.slice(6)}`;
                    return clean.length > 3 ? `${clean.slice(0, 3)}-${clean.slice(3)}` : clean;
                }
                if (requiredLength === 11) {
                    if (clean.length > 7) return `${clean.slice(0, 3)}-${clean.slice(3, 7)}-${clean.slice(7)}`;
                    return clean.length > 3 ? `${clean.slice(0, 3)}-${clean.slice(3)}` : clean;
                }
                return clean;
            },
            onPhoneInput(event) {
                const maxLength = Number(this.requiredPhoneLength || 10);
                const digits = String(event?.target?.value || this.phoneForm.phoneNumber || '')
                    .replace(/\D/g, '')
                    .slice(0, maxLength);
                this.phoneForm.phoneNumber = this.formatPhoneByLength(
                    digits,
                    maxLength
                );
                this.phoneError = '';
                this.phoneSubmitted = false;
                this.emitPhoneValidity();
            },
            formatUsPhone(digits) {
                if (digits.length <= 3) return digits;
                if (digits.length <= 6) return `(${digits.slice(0, 3)})-${digits.slice(3)}`;
                return `(${digits.slice(0, 3)})-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
            },
            emitPhoneValidity() {
                this.$emit('phone-validity-change', this.isPhoneComplete);
            },
            submitPhone() {
                if (this.disabled || this.loading || this.phoneSubmitted) return;
                if (!this.isPhoneComplete) {
                    this.phoneError = `Please enter a valid ${this.requiredPhoneLength}-digit phone number.`;
                    return;
                }
                this.phoneSubmitted = true;
                this.$emit('phone-submit', {
                    countryCodeId: this.phoneForm.countryCodeId,
                    countryCodeText: this.selectedCountryCode?.text || this.phoneForm.countryCodeText,
                    phoneNumber: this.phoneForm.phoneNumber,
                    digits: this.phoneDigits
                });
            },
            onNameFieldInput() {
            },
            confirmFriend() {
                if (this.disabled || this.loading || this.submitted) return;
                this.submitted = true;
                const prefilledCountryText = String(
                    this.prefilled.countryCodeText || this.prefilled.CountryCodeText || ''
                ).trim();
                const resolvedCountryText =
                    (
                        prefilledCountryText &&
                        prefilledCountryText !== 'Loading…' &&
                        prefilledCountryText !== 'Loading...'
                    )
                        ? prefilledCountryText
                        : (
                            this.selectedCountryCode?.text ||
                            this.selectedCountryCodeText ||
                            this.phoneForm.countryCodeText ||
                            ''
                        );
                this.$emit('submit', {
                    ...this.prefilled,
                    firstName: this.editForm.firstName.trim(),
                    lastName: this.editForm.lastName.trim(),
                    email: this.editForm.email.trim(),
                    countryCodeId:
                        this.phoneForm.countryCodeId ||
                        this.prefilled.countryCodeId ||
                        '',
                    countryCodeText: resolvedCountryText,
                    phone: String(this.phoneForm.phoneNumber || '').trim()
                });
            },
            friendKey(friend, index) {
                return String(
                    friend?.friendId ?? friend?.FriendId ?? friend?.id ?? friend?.Id ?? index
                );
            },
            getFriendName(friend) {
                const fullName = [
                    friend?.firstName ?? friend?.FirstName,
                    friend?.lastName ?? friend?.LastName
                ].filter(Boolean).join(' ');
                return this.valueOrDash(fullName || friend?.name || friend?.friendName || friend?.FriendName);
            },
            getFriendEmail(friend) {
                return this.valueOrDash(friend?.email || friend?.Email);
            },
            getFriendPhone(friend) {
                return this.valueOrDash(
                    friend?.phoneNumber || friend?.PhoneNumber || friend?.phone
                );
            },
            getFriendStatus(friend) {
                return this.valueOrDash(friend?.status || friend?.Status || 'Pending');
            },
            friendStatusClass(friend) {
                const status = this.getFriendStatus(friend).toLowerCase();
                if (status === 'accepted' || status === 'active') return 'friend-status-accepted';
                if (status === 'declined' || status === 'inactive') return 'friend-status-declined';
                return 'friend-status-pending';
            },
            getFriendAsset(friend) {
                return this.valueOrDash(
                    friend?.assetName || friend?.AssetName || friend?.propertyName
                );
            },
            getFriendCreatedOn(friend) {
                const raw = friend?.createdOn || friend?.CreatedOn || friend?.created_on;
                if (!raw) {
                    return '-';
                }
                const date = new Date(raw);
                if (isNaN(date.getTime())) {
                    return this.valueOrDash(raw);
                }
                const mm = String(date.getMonth() + 1).padStart(2, '0');
                const dd = String(date.getDate()).padStart(2, '0');
                const yyyy = date.getFullYear();
                return `${mm}-${dd}-${yyyy}`;
            },
            getFriendInitials(friend) {
                const name = this.getFriendName(friend);
                if (!name || name === '-') {
                    const email = this.getFriendEmail(friend);
                    return email && email !== '-' ? email.charAt(0).toUpperCase() : '?';
                }
                const parts = name.trim().split(/\s+/).filter(Boolean);
                if (parts.length === 1) {
                    return parts[0].slice(0, 2).toUpperCase();
                }
                return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
            },
            avatarColor(friend, index) {
                const key = String(
                    friend?.friendId ?? friend?.FriendId ?? friend?.id ?? friend?.Id ??
                    this.getFriendEmail(friend) ?? index
                );
                let hash = 0;
                for (let i = 0; i < key.length; i++) {
                    hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
                }
                const palette = this.avatarPalette;
                return palette[hash % palette.length];
            },
            isDetailsOpen(friend, index) {
                return this.expandedFriendKey === this.friendKey(friend, index);
            },
            toggleDetails(friend, index) {
                const key = this.friendKey(friend, index);
                this.expandedFriendKey = this.expandedFriendKey === key ? '' : key;
            },
            requestDelete(friend) {
                if (this.disabled || this.loading) return;
                this.$emit('delete-friend', { friend });
            }
        }
    };
</script>
<style scoped>
    .friend-flow-wrapper {
        width: 100%;
        font-family: Outfit, sans-serif;
    }

    .friend-card {
        width: 330px;
        max-width: 330px;
        min-width: 300px;
        overflow: hidden;
        background: #ffffff;
        color: #17233d;
        border-radius: 16px;
        box-sizing: border-box;
        border: none;
        box-shadow: none;
        font-size: 13px;
    }

    .friend-card-header {
        background: linear-gradient(135deg, #0f66b8 0%, #122341 100%);
        color: #ffffff;
        padding: 14px 18px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 10px;
        border-bottom: 0;
    }

    .friend-card-title {
        margin: 0;
        font-size: 14px;
        font-weight: 700;
        color: #ffffff;
    }

    .friend-card-subtitle {
        margin: 2px 0 0;
        font-size: 11px;
        line-height: 1.4;
        opacity: 0.8;
        color: #ffffff;
    }

    .friend-card-body {
        padding: 14px 18px;
    }

    .friend-phone-card {
        width: 330px;
        max-width: 330px;
        background: #ffffff;
        color: #333333;
        border-radius: 16px;
        overflow: visible;
        font-family: Outfit, sans-serif;
        font-size: 13px;
        box-sizing: border-box;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    }

    .friend-phone-message {
        width: 100%;
        padding: 12px 16px;
        background: linear-gradient(135deg, #0f66b8 0%, #122341 100%);
        color: #ffffff;
        font-size: 13px;
        font-weight: 600;
        line-height: 1.4;
        box-sizing: border-box;
        border-bottom: 2px solid #f39c12;
        border-radius: 16px 16px 0 0;
    }

    .friend-phone-row {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
        padding: 16px 16px 10px;
        box-sizing: border-box;
        background: #ffffff;
        border-radius: 0;
    }

    .friend-country-dropdown {
        width: 125px;
        min-width: 125px;
        max-width: 125px;
        flex: 0 0 125px;
    }

    .friend-phone-input {
        flex: 1;
        width: 100%;
        min-width: 0;
        height: 40px;
        padding: 8px 10px;
        border: 1px solid #d7dce2;
        border-radius: 8px;
        background: #ffffff;
        color: #1e2d45;
        font-family: Outfit, sans-serif;
        font-size: 13px;
        outline: none;
        box-sizing: border-box;
    }

        .friend-phone-input:focus {
            border-color: #0f66b8;
            box-shadow: 0 0 0 2px rgba(15, 102, 184, 0.12);
        }

    .friend-phone-error {
        padding: 0 16px;
        margin: 0 0 5px;
        color: #dc2626;
        font-size: 11px;
    }

    .friend-phone-submit {
        width: calc(100% - 32px);
        min-height: 40px;
        margin: 4px 16px 16px;
        border: none;
        border-radius: 10px;
        background: linear-gradient(135deg, #0f66b8 0%, #122341 100%);
        color: #ffffff;
        font-size: 13px;
        font-weight: 700;
        cursor: pointer;
    }

        .friend-phone-submit:disabled {
            opacity: 0.45;
            cursor: not-allowed;
        }

    :deep(.friend-country-dropdown.p-dropdown) {
        width: 125px;
        min-width: 125px;
        max-width: 125px;
        height: 40px;
        border: 1px solid #d7dce2;
        border-radius: 8px;
        background: #ffffff;
        box-shadow: none;
    }

    :deep(.friend-country-dropdown .p-dropdown-label) {
        padding: 9px 7px 9px 10px;
        font-size: 13px;
        color: #1e2d45;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    :deep(.friend-country-dropdown .p-dropdown-trigger) {
        width: 28px;
        min-width: 28px;
    }

    :deep(.p-dropdown-panel) {
        min-width: 180px !important;
        max-width: 240px !important;
        z-index: 99999 !important;
    }

    :deep(.p-dropdown-items-wrapper) {
        max-height: 210px !important;
    }

    :deep(.p-dropdown-item) {
        padding: 8px 10px;
        font-size: 12px;
        white-space: nowrap;
    }

    .friend-cancel-btn {
        margin-top: 8px;
        color: #17233d;
        background: #ffffff;
        border: 1px solid #cbd5e1;
    }

    button:disabled,
    input:disabled {
        cursor: not-allowed;
        opacity: 0.55;
    }

    .friend-error {
        margin-top: 8px;
        color: #dc2626;
        font-size: 11px;
    }

    .friend-detail-row {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 10px;
        padding-bottom: 8px;
        margin-bottom: 8px;
        border-bottom: 1px solid #f0f0f0;
    }

        .friend-detail-row.no-border {
            border-bottom: none;
            margin-bottom: 0;
            padding-bottom: 0;
        }

    .friend-label {
        color: #888888;
        font-size: 11px;
        font-weight: 600;
        flex: 0 0 90px;
        min-width: 90px;
    }

    .friend-value {
        flex: 1;
        color: #1e2d45;
        font-size: 12px;
        font-weight: 600;
        text-align: right;
        word-break: break-word;
        overflow-wrap: anywhere;
    }

    .friend-edit-row {
        align-items: center;
    }

    .friend-edit-input {
        flex: 1 1 auto;
        width: 100%;
        max-width: 170px;
        height: 34px;
        padding: 0 9px;
        color: #17233d;
        background: #ffffff;
        border: 1px solid #cbd5e1;
        border-radius: 7px;
        box-sizing: border-box;
        font-family: inherit;
        font-size: 12px;
        font-weight: 600;
        text-align: right;
        outline: none;
    }

        .friend-edit-input::placeholder {
            color: #b5bec9;
            font-weight: 400;
        }

        .friend-edit-input:focus {
            border-color: #0f66b8;
            box-shadow: 0 0 0 2px rgba(15, 102, 184, 0.12);
        }

        .friend-edit-input:disabled {
            background: #f8fafc;
            color: #64748b;
        }

    .friend-preview-country {
        flex: 1 1 auto;
        width: 100%;
        max-width: 170px;
    }

    .friend-preview-phone {
        flex: 1 1 auto;
        width: 100%;
        max-width: 170px;
        height: 34px;
        padding: 0 9px;
        color: #17233d;
        background: #ffffff;
        border: 1px solid #cbd5e1;
        border-radius: 7px;
        box-sizing: border-box;
        font-family: inherit;
        font-size: 12px;
        text-align: right;
        outline: none;
    }

        .friend-preview-phone:focus {
            border-color: #0f66b8;
            box-shadow: 0 0 0 2px rgba(15, 102, 184, 0.12);
        }

    .friend-actions {
        padding: 0 18px 16px;
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .friend-primary-btn {
        width: 100%;
        min-height: 40px;
        background: linear-gradient(135deg, #0f66b8 0%, #122341 100%);
        color: #ffffff;
        border: none;
        border-radius: 10px;
        padding: 10px;
        font-size: 13px;
        font-weight: 700;
        cursor: pointer;
    }

    .friend-cancel-btn {
        width: 100%;
        border: none;
        background: transparent;
        color: #aaaaaa;
        font-size: 12px;
        padding: 6px 10px 0;
        cursor: pointer;
    }

        .friend-primary-btn:disabled,
        .friend-cancel-btn:disabled {
            opacity: 0.45;
            cursor: not-allowed;
            pointer-events: none;
        }

    .friend-list-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .friend-count {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 28px;
        height: 22px;
        padding: 0 8px;
        border-radius: 12px;
        background: #f39c12;
        color: #17233d;
        font-size: 11px;
        font-weight: 700;
    }

    .friend-list {
        max-height: 360px;
        overflow-y: auto;
    }

    .friend-list-item {
        padding: 11px 14px;
        border-bottom: 1px solid #e5e7eb;
    }

    .friend-list-top {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .friend-avatar {
        flex: 0 0 34px;
        width: 34px;
        height: 34px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #ffffff;
        font-size: 12px;
        font-weight: 700;
        text-transform: uppercase;
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
    }

    .friend-info {
        flex: 1;
        min-width: 0;
    }

    .friend-name {
        color: #17233d;
        font-size: 12px;
        font-weight: 700;
    }

    .friend-email {
        margin-top: 2px;
        color: #64748b;
        font-size: 10px;
        overflow-wrap: anywhere;
    }

    .friend-status {
        display: inline-flex;
        padding: 1px 8px;
        border: 1px solid;
        border-radius: 10px;
        font-size: 10px;
        font-weight: 600;
    }

    .friend-status-inline {
        margin-top: 0;
    }

    .friend-status-accepted {
        color: #16a34a;
        background: #dcfce7;
        border-color: #86efac;
    }

    .friend-status-declined {
        color: #dc2626;
        background: #fee2e2;
        border-color: #fca5a5;
    }

    .friend-status-pending {
        color: #b45309;
        background: #fef3c7;
        border-color: #fcd34d;
    }

    .friend-list-actions {
        flex: 0 0 auto;
        display: flex;
        gap: 5px;
    }

    .friend-small-btn {
        padding: 3px 9px;
        color: #17233d;
        background: #ffffff;
        border: 1px solid #cbd5e1;
        border-radius: 12px;
        font-family: inherit;
        font-size: 10px;
        cursor: pointer;
    }

    .friend-remove-btn {
        color: #dc2626;
        border-color: #fecaca;
    }

    .friend-details {
        margin-top: 9px;
        padding-top: 9px;
        border-top: 1px dashed #cbd5e1;
    }

    .friend-empty-state {
        padding: 24px 16px;
        color: #64748b;
        font-size: 12px;
        text-align: center;
    }

    .friend-result-footer {
        display: flex;
        justify-content: space-between;
        padding: 10px 16px;
        color: #64748b;
        background: #f8fafc;
        border-top: 1px solid #e5e7eb;
        font-size: 10px;
    }

    .friend-status-success {
        color: #16a34a;
        font-weight: 700;
    }

    .friend-status-error {
        color: #dc2626;
        font-weight: 700;
    }

    @media (max-width: 480px) {
        .friend-card,
        .friend-phone-card {
            min-width: 0;
            width: 100%;
            max-width: 330px;
        }

        .friend-country-dropdown {
            width: 115px;
            min-width: 115px;
            max-width: 115px;
            flex-basis: 115px;
        }

        :deep(.friend-country-dropdown.p-dropdown) {
            width: 115px;
            min-width: 115px;
            max-width: 115px;
        }
    }
</style>
